import React, { useEffect, useImperativeHandle, useRef } from "react";

import type { ExtractRefType } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import { Avatar } from "../../../../Comment/Avatar";
import InfoTooltip from "../../../../InfoTooltip/InfoTooltip";
import type { MenuPortal } from "./MenuPortal";
import { RoleSelector } from "./RoleSelector";
import type { ShareBadge } from "./ShareBadge";

import style from "./ShareRow.module.css";

type Entity<T> = T extends undefined
  ? {
      name?: string;
      email?: string;
      role?: never;
    }
  : {
      name?: string;
      email?: string;
      role: T;
    };

type ShareRowProps<
  TRoles extends readonly [string, ...string[]],
  TCurrentRole extends TRoles[number] | undefined,
> = {
  roles: TRoles;
  roleDisplayName: (role: TRoles[number]) => string;
  entity: Entity<TCurrentRole>;
  onUpdate?: (prevRole: TCurrentRole, newRole: TRoles[number]) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  isPending?: boolean;
  isError?: boolean;
  onRemove?: () => void;
  sharingBadges?: ShareBadge[];
  immutable?: boolean;
  isRequestAccess?: boolean;
  proRoles?: Set<TRoles[number]>;
  showPaywall?: () => void;
  isProRoleEligible?: boolean;
  tooltipText?: string;
  removeActionText?: string;
};

export type ShareRowRef = {
  closeContextMenu?: () => void;
};

const ShareRowRender = <
  TRoles extends readonly [string, ...string[]],
  TCurrentRole extends TRoles[number] | undefined,
>(
  {
    roles,
    roleDisplayName,
    entity,
    isPending,
    isError,
    onUpdate,
    onRemove,
    containerIntersectionObserver,
    sharingBadges,
    immutable = false,
    isRequestAccess = false,
    proRoles = new Set(),
    showPaywall,
    isProRoleEligible = true,
    removeActionText = "Remove...",
    tooltipText,
  }: ShareRowProps<TRoles, TCurrentRole>,
  ref: React.ForwardedRef<ShareRowRef>
) => {
  const formRef = useRef<HTMLFormElement>(null);
  const menuRef = useRef<ExtractRefType<typeof MenuPortal>>(null);

  useEffect(() => {
    if (formRef.current) {
      containerIntersectionObserver?.observe(formRef.current);
    }
    return () => containerIntersectionObserver?.disconnect();
  }, [containerIntersectionObserver]);

  useImperativeHandle(ref, () => ({
    closeContextMenu: () => {
      menuRef.current?.close?.();
    },
  }));

  return (
    <>
      <form ref={formRef} className={style.shareRow}>
        <Avatar
          disabled={sharingBadges?.includes("Invited") || isRequestAccess}
          title={entity.name ?? entity.email ?? "never"}
        />
        <div
          className={classNames(
            style.nameContainer,
            (sharingBadges?.includes("Invited") || isRequestAccess) && style.pending
          )}
        >
          <div>
            <div className={style.nameText}>{entity.name || entity.email}</div>
            {sharingBadges?.map((badge) => (
              <span className={style.badge} key={badge}>
                ({badge})
              </span>
            ))}
          </div>
          {entity.name && (
            <div title={entity.email} className={style.emailText}>
              {entity.email}
            </div>
          )}
        </div>
        <div className={style.roleContainer}>
          {tooltipText && <InfoTooltip content={tooltipText} />}
          {immutable && entity.role && (
            <div className={style.immutableRow}>{roleDisplayName(entity.role)}</div>
          )}
          {!immutable && (
            <RoleSelector
              menuRef={menuRef}
              isPending={isPending}
              roleDisplayName={roleDisplayName}
              roles={roles}
              roleWatch={entity.role}
              onRemove={onRemove}
              removeActionText={removeActionText}
              onClick={(newRole) => {
                if (isPending || newRole === entity.role) return;

                if (proRoles.has(newRole) && !isProRoleEligible) {
                  showPaywall?.();
                  return;
                }
                onUpdate?.(entity.role as TCurrentRole, newRole);
              }}
            />
          )}
        </div>
      </form>

      {isError && <span className={style.red}>{"Something went wrong. Please try again."}</span>}
    </>
  );
};

interface ShareRowComponent {
  <
    TRoles extends readonly [string, ...string[]],
    TCurrentRole extends TRoles[number] | undefined = undefined,
  >(
    props: ShareRowProps<TRoles, TCurrentRole> & React.RefAttributes<ShareRowRef>
  ): React.ReactElement;
}

export const ShareRow = React.forwardRef(ShareRowRender) as ShareRowComponent;
